/**
 * Optimizer Control Center — search + uniform dropdown filters.
 * Provider / Mode / Status / Country / Tag are all the same compact dropdown,
 * so the bar stays minimal and consistent as filters grow.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useMemo } from 'react';
import { Search } from 'lucide-react';

import { Card, CardContent, Input } from '@/components/ui';
import { useStaticTags } from '@/hooks';
import { FilterSelect } from '@/features/optimizer-center/components/FilterSelect';
import {
  MODE_OPTIONS,
  STATUS_OPTIONS,
  buildCountryOptions,
  buildProviderOptions,
  buildTagOptions,
} from '@/features/optimizer-center/utils/filter-options';
import type {
  OptimizerFilters,
  OptimizerRow,
} from '@/features/optimizer-center/optimizer-center.types';
import { useFlowOptimizerTrackers } from '@/features/trackers/flow-optimizer';

export interface OptimizerFiltersCardProps {
  rows: OptimizerRow[];
  filters: OptimizerFilters;
  onChange: (filters: OptimizerFilters) => void;
  /** Hide the Country select — used when the country is pinned (e.g. the SMSWiser country page). */
  hideCountry?: boolean;
}

export const OptimizerFiltersCard: FC<OptimizerFiltersCardProps> = ({
  rows,
  filters,
  onChange,
  hideCountry = false,
}) => {
  const { data: tags } = useStaticTags();
  const trackers = useFlowOptimizerTrackers();
  const providerOptions = useMemo(() => buildProviderOptions(trackers), [trackers]);
  const countryOptions = useMemo(() => buildCountryOptions(rows), [rows]);
  const tagOptions = useMemo(() => buildTagOptions(tags ?? []), [tags]);
  const update = (key: keyof OptimizerFilters, value: string) =>
    onChange({ ...filters, [key]: value } as OptimizerFilters);

  const selects = (
    [
      { key: 'provider', options: providerOptions, placeholder: 'All providers' },
      { key: 'mode', options: MODE_OPTIONS, placeholder: 'All modes' },
      { key: 'status', options: STATUS_OPTIONS, placeholder: 'All statuses' },
      { key: 'country', options: countryOptions, placeholder: 'All countries' },
      { key: 'tag', options: tagOptions, placeholder: 'All tags' },
    ] as const
  ).filter((s) => !hideCountry || s.key !== 'country');

  return (
    <Card>
      <CardContent className="flex flex-wrap items-center gap-2 p-3">
        <div className="relative w-full sm:w-72 lg:w-96">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search campaign, flow or ID…"
            value={filters.search}
            onChange={(e) => update('search', e.target.value)}
            className="h-9 pl-9"
          />
        </div>
        {selects.map((s) => (
          <FilterSelect
            key={s.key}
            options={s.options}
            value={filters[s.key]}
            onChange={(v) => update(s.key, v)}
            placeholder={s.placeholder}
          />
        ))}
      </CardContent>
    </Card>
  );
};
